import { useState, useEffect } from 'react';
import { Modal } from '../Modal';
import { servicesService } from '../../services/api';

const LOCATION_MODES = [
  { value: 'in_person', label: 'In person' },
  { value: 'phone', label: 'Phone call' },
  { value: 'video', label: 'Video call' },
  { value: 'client_location', label: "Client's location" }
];

const INITIAL = {
  name: '', description: '', duration_minutes: 30, price: '', location_mode: 'in_person',
  color: '', buffer_before_minutes: 0, buffer_after_minutes: 0,
  min_notice_minutes: 1440, max_advance_days: 60, is_public: true, is_active: true
};

export default function ServiceModal({ isOpen, onClose, onSave, service = null }) {
  const isEdit = !!service;
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(service ? { ...INITIAL, ...service, price: service.price ?? '' } : INITIAL);
      setErrors({});
    }
  }, [isOpen, service]);

  function change(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setErrors({ name: 'Name is required' }); return; }
    if (!form.duration_minutes || form.duration_minutes < 5) { setErrors({ duration_minutes: 'Duration must be at least 5 minutes' }); return; }
    setSaving(true);
    const payload = {
      ...form,
      duration_minutes: Number(form.duration_minutes),
      price: form.price === '' ? null : Number(form.price),
      buffer_before_minutes: Number(form.buffer_before_minutes) || 0,
      buffer_after_minutes: Number(form.buffer_after_minutes) || 0,
      min_notice_minutes: Number(form.min_notice_minutes) || 0,
      max_advance_days: Number(form.max_advance_days) || 60
    };
    try {
      const response = isEdit
        ? await servicesService.update(service.id, payload)
        : await servicesService.create(payload);
      if (response.success) { onSave?.(response.service); onClose(); }
      else setErrors({ submit: response.error || 'Failed to save service' });
    } catch (err) {
      setErrors({ submit: 'An error occurred. Please try again.' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Edit Meeting Type' : 'New Meeting Type'} size="large">
      <form onSubmit={submit} className="service-form">
        <div className="form-group">
          <label htmlFor="name">Name *</label>
          <input id="name" name="name" value={form.name} onChange={change} placeholder="e.g. Advertising Consultation" autoFocus />
          {errors.name && <span className="form-error">{errors.name}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea id="description" name="description" rows="2" value={form.description} onChange={change} placeholder="What this meeting is for…" />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="duration_minutes">Duration (minutes) *</label>
            <input id="duration_minutes" name="duration_minutes" type="number" min="5" step="5" value={form.duration_minutes} onChange={change} />
            {errors.duration_minutes && <span className="form-error">{errors.duration_minutes}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="price">Price (optional)</label>
            <input id="price" name="price" type="number" min="0" step="0.01" value={form.price} onChange={change} placeholder="Leave blank for free" />
          </div>
          <div className="form-group">
            <label htmlFor="location_mode">Location</label>
            <select id="location_mode" name="location_mode" value={form.location_mode} onChange={change}>
              {LOCATION_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="buffer_before_minutes">Buffer before (min)</label>
            <input id="buffer_before_minutes" name="buffer_before_minutes" type="number" min="0" step="5" value={form.buffer_before_minutes} onChange={change} />
          </div>
          <div className="form-group">
            <label htmlFor="buffer_after_minutes">Buffer after (min)</label>
            <input id="buffer_after_minutes" name="buffer_after_minutes" type="number" min="0" step="5" value={form.buffer_after_minutes} onChange={change} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="min_notice_minutes">Min notice (min)</label>
            <input id="min_notice_minutes" name="min_notice_minutes" type="number" min="0" step="30" value={form.min_notice_minutes} onChange={change} />
          </div>
          <div className="form-group">
            <label htmlFor="max_advance_days">Book up to (days ahead)</label>
            <input id="max_advance_days" name="max_advance_days" type="number" min="1" value={form.max_advance_days} onChange={change} />
          </div>
        </div>

        <div className="form-row">
          <label className="checkbox-label">
            <input type="checkbox" name="is_public" checked={!!form.is_public} onChange={change} /> Clients can book this online
          </label>
          <label className="checkbox-label">
            <input type="checkbox" name="is_active" checked={!!form.is_active} onChange={change} /> Active
          </label>
        </div>

        {errors.submit && <div className="form-error text-center mb-md">{errors.submit}</div>}

        <div className="modal-actions">
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Save' : 'Create')}</button>
        </div>
      </form>
    </Modal>
  );
}
